import React, { Component } from "react";
import { observer } from "mobx-react";
import { TextField, Select, MenuItem, FormControl, InputAdornment, Checkbox, ListItemText, Button, CircularProgress } from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import "./StaffActRoster.css";

@observer
class StaffActRosterToolbar extends Component {
    state = {
        searchTerm: "",
        actTypeFilter: "all",
        lineupFilter: "all",
        selectedVibes: [],
        selectedGenres: [],
        ...(this.props.filters || {})
    };

    componentDidUpdate(prevProps) {
        if (this.props.filters && prevProps.filters !== this.props.filters) this.setState(this.props.filters);
    }

    handleSearchChange = (e) => {
        const value = e.target.value;
        this.setState({ searchTerm: value }, () => {
            this.notifyFiltersChanged();
        });
    };

    handleClearSearch = () => {
        this.setState({ searchTerm: "" }, () => {
            this.notifyFiltersChanged();
        });
    };

    handleActTypeChange = (e) => {
        this.setState({ actTypeFilter: e.target.value }, () => {
            this.notifyFiltersChanged();
        });
    };

    handleLineupChange = (e) => {
        this.setState({ lineupFilter: e.target.value }, () => {
            this.notifyFiltersChanged();
        });
    };

    handleVibeToggle = (vibe) => {
        const { selectedVibes } = this.state;
        const isSelected = selectedVibes.includes(vibe);
        const newVibes = isSelected 
            ? selectedVibes.filter(v => v !== vibe)
            : [...selectedVibes, vibe];
        
        this.setState({ selectedVibes: newVibes }, () => {
            this.notifyFiltersChanged();
        });
    };

    handleGenresChange = event => {
        const selected = event.target.value;
        this.setState({ selectedGenres: selected.includes("") ? [] : selected }, this.notifyFiltersChanged);
    };

    handleClearAllFilters = () => {
        this.setState({
            searchTerm: "",
            actTypeFilter: "all",
            lineupFilter: "all",
            selectedVibes: [],
            selectedGenres: []
        }, () => {
            this.notifyFiltersChanged();
        });
    };

    notifyFiltersChanged = () => {
        const { onFiltersChange } = this.props;
        if (onFiltersChange) {
            onFiltersChange({
                searchTerm: this.state.searchTerm,
                actTypeFilter: this.state.actTypeFilter,
                lineupFilter: this.state.lineupFilter,
                selectedVibes: this.state.selectedVibes,
                selectedGenres: this.state.selectedGenres
            });
        }
    };

    hasActiveFilters = () => {
        const { searchTerm, actTypeFilter, lineupFilter, selectedVibes, selectedGenres } = this.state;
        return searchTerm !== "" || actTypeFilter !== "all" || lineupFilter !== "all" || selectedVibes.length > 0 || selectedGenres.length > 0;
    };

    getUniqueActTypes = () => {
        return this.props.actTypes || [];
    };

    getUniqueLineups = () => {
        return this.props.lineups || [];
    };

    getUniqueVibes = () => {
        return this.props.vibes || [];
    };

    render() {
        const { totalCount, filteredCount, disabled, catalogsLoading, catalogsError } = this.props;
        const { searchTerm, actTypeFilter, lineupFilter, selectedVibes, selectedGenres } = this.state;
        const genres = [...(this.props.genres || [])].sort((a, b) => a.name.localeCompare(b.name));
        const catalogsDisabled = disabled || catalogsLoading || catalogsError;
        
        const actTypes = this.getUniqueActTypes();
        const lineups = this.getUniqueLineups();
        const vibes = this.getUniqueVibes();
        const hasFilters = this.hasActiveFilters();

        return (
            <div className="staff-roster-toolbar">
                <div className="staff-toolbar-row">
                    <div className="staff-search-box">
                        <TextField
                            placeholder="Search acts..."
                            value={searchTerm}
                            onChange={this.handleSearchChange}
                            disabled={disabled}
                            variant="outlined"
                            size="small"
                            className="staff-search-input"
                            InputProps={{
                                style: { backgroundColor: "white" },
                                startAdornment: (
                                    <InputAdornment position="start" style={{ backgroundColor: "transparent", background: "none" }}>
                                        <SearchIcon style={{ color: "#999", fontSize: 20 }} />
                                    </InputAdornment>
                                ),
                                endAdornment: searchTerm && (
                                    <InputAdornment position="end" style={{ backgroundColor: "transparent", background: "none" }}>
                                        <ClearIcon 
                                            style={{ color: "#999", fontSize: 18, cursor: "pointer" }} 
                                            onClick={this.handleClearSearch}
                                        />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </div>

                    <div className="staff-filter-group">
                        <span className="staff-filter-label">Type:</span>
                        <FormControl variant="outlined" size="small" className="staff-filter-select">
                            <Select
                                value={actTypeFilter}
                                onChange={this.handleActTypeChange}
                                disabled={catalogsDisabled}
                                inputProps={{ "aria-label": "Act type filter" }}
                            >
                                <MenuItem value="all">All Types</MenuItem>
                                {actTypes.map(type => (
                                    <MenuItem key={type.id} value={type.id}>{type.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>

                    <div className="staff-filter-group">
                        <span className="staff-filter-label">Lineup:</span>
                        <FormControl variant="outlined" size="small" className="staff-filter-select">
                            <Select
                                value={lineupFilter}
                                onChange={this.handleLineupChange}
                                disabled={disabled}
                                inputProps={{ "aria-label": "Lineup filter" }}
                            >
                                <MenuItem value="all">Any Size</MenuItem>
                                {lineups.map(lineup => (
                                    <MenuItem key={lineup.id} value={lineup.id}>{lineup.name}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </div>

                    {vibes.length > 0 && (
                        <div className="staff-vibe-filters">
                            {vibes.map(vibe => (
                                <button
                                    key={vibe.id}
                                    disabled={catalogsDisabled}
                                    aria-pressed={selectedVibes.includes(vibe.id)}
                                    className={`staff-vibe-chip ${selectedVibes.includes(vibe.id) ? 'active' : ''}`}
                                    onClick={() => this.handleVibeToggle(vibe.id)}
                                >
                                    {vibe.name}
                                    {selectedVibes.includes(vibe.id) && (
                                        <span className="remove-icon"><i className="fa fa-times"></i></span>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="staff-filter-group">
                        <span className="staff-filter-label">Genres:</span>
                        <FormControl className="staff-filter-select">
                            <Select multiple displayEmpty value={selectedGenres}
                                disabled={catalogsDisabled || genres.length === 0}
                                onChange={this.handleGenresChange}
                                inputProps={{ "aria-label": "Genre filter" }}
                                renderValue={selected => selected.length ? selected.map(id =>
                                    genres.find(genre => String(genre.id) === String(id))?.name || String(id)
                                ).join(", ") : "All Genres"}>
                                <MenuItem value="">
                                    <Checkbox checked={selectedGenres.length === 0} color="primary" />
                                    <ListItemText primary="All Genres" />
                                </MenuItem>
                                {genres.map(genre => <MenuItem key={genre.id} value={genre.id}>
                                    <Checkbox checked={selectedGenres.includes(genre.id)} color="primary" />
                                    <ListItemText primary={genre.name} />
                                </MenuItem>)}
                            </Select>
                        </FormControl>
                    </div>
                    {hasFilters && (
                        <button disabled={disabled} className="staff-clear-filters" onClick={this.handleClearAllFilters}>
                            Clear All
                        </button>
                    )}

                    <span className="staff-results-count">
                        {hasFilters 
                            ? `${filteredCount} matching acts shown`
                            : `${totalCount} acts shown`
                        }
                    </span>
                </div>
                {catalogsLoading && <div role="status"><CircularProgress size={12} /> Loading filters...</div>}
                {catalogsError && <div role="alert">
                    Unable to load filter options.
                    <Button size="small" onClick={this.props.onRetryCatalogs}>Retry filters</Button>
                </div>}
                {!catalogsLoading && !catalogsError && genres.length === 0 && <div role="status">No genres available.</div>}
            </div>
        );
    }
}

export default StaffActRosterToolbar;
